import { GridApi } from "ag-grid-community";
import { MutableRefObject, useCallback, useEffect, useRef } from "react";
import { applyFlexToLastColumn } from "../AgGridUtils";

interface UseColumnSizingParams {
  gridApiRef: MutableRefObject<GridApi | null>;
  rowData: any[] | undefined;
  columnDefs: any[] | undefined;
}

export const useColumnSizing = ({ gridApiRef, rowData, columnDefs }: UseColumnSizingParams) => {
  const sizingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fitOrAutoSizeColumns = useCallback((api: GridApi) => {
    if (sizingTimerRef.current) clearTimeout(sizingTimerRef.current);
    sizingTimerRef.current = setTimeout(() => {
      sizingTimerRef.current = null;
      api.autoSizeAllColumns();
      requestAnimationFrame(() => {
        const allCols = api.getColumns();
        if (!allCols) return;
        const totalColsWidth = allCols.reduce(
          (sum, col) => (col.isVisible() ? sum + col.getActualWidth() : sum),
          0,
        );
        const gridEl = api.getGridId ? document.getElementById(api.getGridId()) : null;
        const containerEl =
          gridEl ?? (document.querySelector(".ag-body-viewport") as HTMLElement);
        const availableWidth = containerEl?.clientWidth ?? 0;
        if (availableWidth > 0 && totalColsWidth < availableWidth) {
          api.sizeColumnsToFit();
        }
      });
    }, 50);
  }, []);

  const onColumnRowGroupChanged = useCallback(() => {
    if (gridApiRef.current) applyFlexToLastColumn(gridApiRef.current);
  }, [gridApiRef]);

  useEffect(() => {
    if (gridApiRef.current && rowData?.length) {
      fitOrAutoSizeColumns(gridApiRef.current);
    }
  }, [rowData, columnDefs, fitOrAutoSizeColumns, gridApiRef]);

  return { fitOrAutoSizeColumns, onColumnRowGroupChanged };
};
